import { THEME } from '@Theme';
import { StyleSheet } from 'aphrodite';

const toneText = {
	green: { color: THEME.colors.success.dark },
	amber: { color: THEME.colors.warning.dark },
	red: { color: THEME.colors.danger.dark },
	gray: { color: THEME.colors.text.light },
} as const;

const toneDot = {
	green: { backgroundColor: THEME.colors.success.dark },
	amber: { backgroundColor: THEME.colors.warning.dark },
	red: { backgroundColor: THEME.colors.danger.main },
	gray: { backgroundColor: THEME.colors.icon.gray },
} as const;

export const styleSheet = StyleSheet.create({
	// The ghost variant has no border, background or underline, so without this it reads as static text.
	quietAction: {
		textDecoration: 'underline',
		textUnderlineOffset: 2,
		color: THEME.colors.text.light,
		':hover': {
			color: THEME.colors.text.main,
		},
	},
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xl,
		marginTop: THEME.spacing.sm,
		marginBottom: THEME.spacing.xxl,
	},
	emptyMessage: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		padding: `${THEME.spacing.md}px 0`,
	},
	skeletonBanner: {
		height: 120,
		borderRadius: THEME.common.borderRadius,
	},
	skeletonBar: {
		height: 48,
		borderRadius: THEME.common.borderRadius,
	},

	verdict: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		padding: THEME.spacing.lg,
		borderRadius: THEME.common.borderRadius,
		border: `1px solid ${THEME.colors.border.main}`,
		background: THEME.colors.background.light,
	},
	verdictFail: {
		background: THEME.colors.danger.light,
		borderColor: THEME.colors.danger.main,
	},
	verdictPass: {
		background: THEME.colors.success.light,
		borderColor: THEME.colors.success.main,
	},
	verdictWarn: {
		background: THEME.colors.warning.light,
		borderColor: THEME.colors.warning.main,
	},
	verdictInfo: {
		background: THEME.colors.info.light,
		borderColor: THEME.colors.info.main,
	},
	verdictNeutral: {
		background: THEME.colors.background.main,
	},
	verdictHead: {
		display: 'flex',
		gap: THEME.spacing.md,
		alignItems: 'flex-start',
	},
	verdictIcon: {
		fontSize: THEME.typography.fontSizes.lg,
		lineHeight: 1.3,
		flexShrink: 0,
	},
	verdictHeadline: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.dark,
	},
	verdictModeChip: {
		display: 'inline-block',
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.standard,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		border: '1px solid currentColor',
		borderRadius: THEME.common.borderRadiusSm,
		padding: '2px 6px',
		opacity: 0.75,
		marginLeft: THEME.spacing.sm,
		whiteSpace: 'nowrap',
		verticalAlign: 'middle',
	},
	verdictSub: {
		margin: `${THEME.spacing.xs}px 0 0`,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.dark,
	},
	verdictReason: {
		margin: `${THEME.spacing.xs}px 0 0`,
		fontSize: THEME.typography.fontSizes.sm,
		fontStyle: 'italic',
		color: THEME.colors.text.dark,
	},
	verdictNext: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.sm,
		background: THEME.colors.background.light,
		borderLeft: `3px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadiusSm,
		padding: `10px 14px`,
		color: THEME.colors.text.main,
	},
	actions: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		alignItems: 'center',
	},

	section: {
		display: 'flex',
		flexDirection: 'column',
	},
	sectionHead: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'baseline',
		gap: THEME.spacing.md,
		paddingBottom: THEME.spacing.sm,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		marginBottom: THEME.spacing.md,
	},
	sectionTitle: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		letterSpacing: '0.1em',
		textTransform: 'uppercase',
		color: THEME.colors.text.light,
	},
	sectionCount: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},

	statusBox: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
	},
	statusBar: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: `${THEME.spacing.sm}px ${THEME.spacing.xl}px`,
		width: '100%',
		padding: `12px ${THEME.spacing.md}px`,
		background: 'none',
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		fontSize: THEME.typography.fontSizes.sm,
		textAlign: 'left',
		cursor: 'pointer',
		':hover': {
			background: THEME.colors.background.main,
		},
	},
	statusItem: {
		display: 'flex',
		alignItems: 'baseline',
		gap: THEME.spacing.sm,
	},
	statusWho: {
		fontSize: THEME.typography.fontSizes.xxs,
		letterSpacing: '0.09em',
		textTransform: 'uppercase',
		color: THEME.colors.text.light,
	},
	statusState: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	statusStateGreen: toneText.green,
	statusStateAmber: toneText.amber,
	statusStateRed: toneText.red,
	statusStateGray: toneText.gray,
	statusNote: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	statusToggle: {
		marginLeft: 'auto',
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.primary.main,
	},
	statusPanel: {
		padding: THEME.spacing.md,
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	statusGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 258px), 1fr))',
		gap: THEME.spacing.md,
		alignItems: 'flex-start',
	},
	statusCard: {
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		padding: THEME.spacing.md,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	statusCardState: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	dot: {
		width: 9,
		height: 9,
		borderRadius: '50%',
		flexShrink: 0,
	},
	dotGreen: toneDot.green,
	dotAmber: toneDot.amber,
	dotRed: toneDot.red,
	dotGray: toneDot.gray,
	statusCardRows: {
		margin: 0,
		display: 'grid',
		gridTemplateColumns: 'auto 1fr',
		gap: `3px ${THEME.spacing.sm}px`,
		fontSize: THEME.typography.fontSizes.xs,
	},
	statusCardRowLabel: {
		color: THEME.colors.text.light,
	},
	statusCardRowValue: {
		margin: 0,
		wordBreak: 'break-word',
	},
	statusCardError: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.danger.dark,
		background: THEME.colors.danger.light,
		borderRadius: THEME.common.borderRadiusSm,
		padding: '8px 10px',
	},
	statusCardNote: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	panelActions: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		marginTop: THEME.spacing.md,
	},

	group: {
		marginBottom: THEME.spacing.md,
	},
	groupTrigger: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		width: '100%',
		padding: `${THEME.spacing.sm}px 0`,
		background: 'none',
		border: 'none',
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		textAlign: 'left',
		cursor: 'pointer',
	},
	groupChevron: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
	},
	groupCount: {
		fontWeight: THEME.typography.fontWeights.standard,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	groupBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.sm}px 0 ${THEME.spacing.md}px 20px`,
	},

	finding: {
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		padding: THEME.spacing.md,
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
	},
	findingResolved: {
		opacity: 0.72,
	},
	findingTop: {
		display: 'flex',
		gap: THEME.spacing.sm,
		alignItems: 'flex-start',
		flexWrap: 'wrap',
	},
	findingTitle: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		flex: '1 1 16rem',
		minWidth: 0,
	},
	findingMeta: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '2px 10px',
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	metaLink: {
		color: THEME.colors.primary.dark,
		textDecoration: 'underline',
		textUnderlineOffset: 2,
		':hover': {
			color: THEME.colors.primary.main,
		},
	},
	evidence: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		margin: 0,
		padding: 0,
		listStyle: 'none',
	},
	evidenceItem: {
		display: 'flex',
		gap: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.sm,
	},
	evidenceSource: {
		fontWeight: THEME.typography.fontWeights.bold,
		flexShrink: 0,
	},
	fix: {
		background: THEME.colors.hover.light,
		borderLeft: `3px solid ${THEME.colors.primary.main}`,
		borderRadius: THEME.common.borderRadiusSm,
		padding: '10px 12px',
		fontSize: THEME.typography.fontSizes.sm,
		display: 'flex',
		gap: THEME.spacing.md,
		alignItems: 'flex-start',
		justifyContent: 'space-between',
	},
	fixLabel: {
		display: 'block',
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.semibold,
		letterSpacing: '0.09em',
		textTransform: 'uppercase',
		color: THEME.colors.primary.dark,
		marginBottom: 3,
	},
	fixLinks: {
		display: 'flex',
		gap: THEME.spacing.sm,
		alignItems: 'center',
		flexShrink: 0,
		flexWrap: 'wrap',
		justifyContent: 'flex-end',
	},
	fixGuidanceLink: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.primary.dark,
		textDecoration: 'underline',
		textUnderlineOffset: 2,
		whiteSpace: 'nowrap',
		':hover': {
			color: THEME.colors.primary.main,
		},
	},

	historyScroller: {
		overflowX: 'auto',
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
	},
	historyTable: {
		width: '100%',
		borderCollapse: 'collapse',
		fontSize: THEME.typography.fontSizes.xs,
	},
	historyHeadCell: {
		textAlign: 'left',
		padding: '8px 12px',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.semibold,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: THEME.colors.text.light,
	},
	historyCell: {
		textAlign: 'left',
		padding: '8px 12px',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	historyWhen: {
		whiteSpace: 'nowrap',
	},
	historyReleaseCell: {
		background: THEME.colors.warning.light,
	},

	dialogStack: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	dialogText: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.sm,
	},
	warnBox: {
		margin: 0,
		background: THEME.colors.warning.light,
		border: `1px solid ${THEME.colors.warning.main}`,
		borderRadius: THEME.common.borderRadiusSm,
		padding: '12px 14px',
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.dark,
	},
	blockerList: {
		margin: 0,
		paddingLeft: 18,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		fontSize: THEME.typography.fontSizes.sm,
	},
	blockerListCode: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	registrationFormLink: {
		color: THEME.colors.primary.main,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	checkboxLabel: {
		fontSize: THEME.typography.fontSizes.sm,
	},
});
